// ひょうじ（一覧レイアウト）切替。フィルタ・並び替えと同じく、トリガー → 中央 Modal で
// 下書きを選び「かくにん」で適用 /「やめる」or 背景タップで閉じる。
import { useState } from "react";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { PieceLayoutMode } from "features/builder/pieceList";
import { SidebarDropdown } from "./SidebarDropdown";

// 一覧コンテナに当てるグリッドクラス（カラム数 / 圧縮リスト）
export const LAYOUT_GRID_CLASS: Record<PieceLayoutMode, string> = {
  [PieceLayoutMode.GRID_1]: "grid grid-cols-1 gap-4",
  [PieceLayoutMode.GRID_2]: "grid grid-cols-2 gap-4",
  [PieceLayoutMode.LIST]: "grid grid-cols-1 gap-2",
};

const MODE_ITEMS: { mode: PieceLayoutMode; icon: string; labelKey: string }[] = [
  { mode: PieceLayoutMode.GRID_1, icon: "crop_portrait", labelKey: "MSG_BUILDER_LAYOUT_GRID_1" },
  { mode: PieceLayoutMode.GRID_2, icon: "grid_view", labelKey: "MSG_BUILDER_LAYOUT_GRID_2" },
  { mode: PieceLayoutMode.LIST, icon: "view_list", labelKey: "MSG_BUILDER_LAYOUT_LIST" },
];

const LAYOUT_LABEL_KEY: Record<PieceLayoutMode, string> = {
  [PieceLayoutMode.GRID_1]: "MSG_BUILDER_LAYOUT_GRID_1",
  [PieceLayoutMode.GRID_2]: "MSG_BUILDER_LAYOUT_GRID_2",
  [PieceLayoutMode.LIST]: "MSG_BUILDER_LAYOUT_LIST",
};

const LAYOUT_ICON: Record<PieceLayoutMode, string> = {
  [PieceLayoutMode.GRID_1]: "crop_portrait",
  [PieceLayoutMode.GRID_2]: "grid_view",
  [PieceLayoutMode.LIST]: "view_list",
};

// Modal の中身（開くたび再マウントされ、下書きは適用済みから初期化）。
// 選択肢は元の LayoutModeSwitch と同じ「白地＋色枠／選択時は塗りつぶし」の見えるボタン。
const LayoutPanel = ({
  value,
  accentTextClass,
  accentBgClass,
  accentBorderClass,
  onApply,
  onClose,
}: {
  value: PieceLayoutMode;
  accentTextClass: string;
  accentBgClass: string;
  accentBorderClass: string;
  onApply: (mode: PieceLayoutMode) => void;
  onClose: () => void;
}) => {
  const play = usePlaySound();
  const [draft, setDraft] = useState(value);
  const handleApply = () => {
    play();
    btnDelay(() => {
      onApply(draft);
      onClose();
    });
  };
  const handleCancel = () => {
    play();
    btnDelay(onClose);
  };
  return (
    <div className="flex-col-el items-start w-full gap-3">
      <div className="flex-col-el w-full gap-2">
        {MODE_ITEMS.map((item) => {
          const active = draft === item.mode;
          return (
            <button
              key={item.mode}
              type="button"
              onClick={() => {
                play();
                setDraft(item.mode);
              }}
              className={`type-inherit-font flex-row-el items-center gap-2 w-full px-3 py-2 rounded-xl border-solid border-2 cursor-pointer ${
                active ? `${accentBgClass} ${accentBorderClass}` : `bg-white ${accentBorderClass}`
              }`}
            >
              <span
                className={`material-symbols-outlined type-md shrink-0 ${
                  active ? "text-white" : accentTextClass
                }`}
              >
                {item.icon}
              </span>
              <p
                className={`type-text type-sm whitespace-nowrap flex-1 min-w-0 truncate !text-left ${
                  active ? "text-white" : "text-textcolor-black"
                }`}
              >
                {I18n.t(item.labelKey)}
              </p>
            </button>
          );
        })}
      </div>
      {/* フッター：削除ダイアログと同じ .upsilon デザインシステムボタン（確定は色付き） */}
      <div className="flex-row-el flex-center justify-end w-full gap-2 upsilon">
        <button type="button" onClick={handleCancel} className="type-inherit-font btn btn-blue">
          <div className="flex-row-center gap-1">
            <span className="material-symbols-outlined text-white type-md">close</span>
            <p className="type-text type-body text-white whitespace-nowrap">
              {I18n.t("MSG_STOP_BTN")}
            </p>
          </div>
        </button>
        <button type="button" onClick={handleApply} className="type-inherit-font btn !bg-green-100">
          <div className="flex-row-center gap-1">
            <span className="material-symbols-outlined text-white type-md">check</span>
            <p className="type-text type-body text-white whitespace-nowrap">
              {I18n.t("MSG_CONFIRM_BTN")}
            </p>
          </div>
        </button>
      </div>
    </div>
  );
};

export const LayoutModeMenu = ({
  value,
  accentTextClass,
  accentBgClass,
  accentBorderClass,
  onChange,
}: {
  value: PieceLayoutMode;
  accentTextClass: string;
  accentBgClass: string;
  accentBorderClass: string;
  onChange: (mode: PieceLayoutMode) => void;
}) => (
  <SidebarDropdown
    icon={LAYOUT_ICON[value]}
    label={LAYOUT_LABEL_KEY[value]}
    modalTitle="MSG_BUILDER_SECTION_LAYOUT"
    modalIcon="grid_view"
    accentTextClass={accentTextClass}
    accentBgClass={accentBgClass}
    accentBorderClass={accentBorderClass}
    active
    wide
  >
    {(close) => (
      <LayoutPanel
        value={value}
        accentTextClass={accentTextClass}
        accentBgClass={accentBgClass}
        accentBorderClass={accentBorderClass}
        onApply={onChange}
        onClose={close}
      />
    )}
  </SidebarDropdown>
);
